import axios, { AxiosInstance } from 'axios'

export interface ReviewData {
  text: string
  rating: number
  reviewer?: string
  reviewer_email?: string
  escalation_email?: string
}

export interface IntentAnalysis {
  sentiment: 'positive' | 'negative' | 'neutral'
  topics: string[]
  urgency: 'low' | 'medium' | 'high'
  key_points: string[]
  requires_escalation: boolean
}

export interface ValidationResult {
  is_valid: boolean
  score: number
  issues?: string[]
  feedback?: string
}

export interface EscalationResult {
  should_escalate: boolean
  priority: 'low' | 'medium' | 'high'
  reasons: string[]
}

export interface ProcessingResult {
  intent: IntentAnalysis
  response: string
  validation: ValidationResult
  escalation: EscalationResult
  status: 'posted' | 'escalated' | 'failed' | 'pending_approval'
  email_sent?: boolean
  approval_token?: string
}

// ── SMB Demo types ────────────────────────────────────────────────────────────

export interface SmbFetchRequest {
  business_name: string
  location: string
  limit?: number
  brand_voice?: string
  process_reviews?: boolean
}

export interface SmbReview {
  reviewer: string
  rating: number
  text: string | null
  date: string
  has_owner_reply: boolean
  is_text_review: boolean
  review_id?: string | null
  owner_reply?: string | null
  owner_reply_date?: string | null
  ai_response?: string | null
  sentiment?: string | null
  topics?: string[]
  urgency?: string | null
  validation_score?: number | null
  should_escalate?: boolean | null
  escalation_reasons?: string[]
  status?: string | null
  processing_time_ms?: number | null
}

export interface SmbSummary {
  total_reviews: number
  text_reviews: number
  star_only_reviews: number
  already_replied: number
  unanswered_before_tool?: number
  ai_responses_generated: number
  would_auto_post: number
  would_escalate: number
  sentiment_breakdown: Record<string, number>
  avg_validation_score: number
  avg_processing_time_ms: number
}

export interface SmbFetchResponse {
  place_id: string
  business_name: string
  address: string
  overall_rating: number
  total_reviews: number
  fetched_count: number
  processed_count: number
  reviews: SmbReview[]
  summary: SmbSummary
  demo_note: string
}

class APIClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: '/api',
      timeout: 30000,
    })
  }

  async processReview(review: ReviewData): Promise<ProcessingResult> {
    try {
      const response = await this.client.post('/reviews/test/process', {
        text: review.text,
        rating: review.rating,
        reviewer: review.reviewer || 'Anonymous',
        reviewer_email: review.reviewer_email,
        escalation_email: review.escalation_email,
      })
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async pollApprovalStatus(token: string): Promise<{ status: 'pending' | 'approved' | 'rejected' }> {
    try {
      const response = await this.client.get(`/reviews/approval-status?token=${token}`)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async fetchSmbReviews(request: SmbFetchRequest): Promise<SmbFetchResponse> {
    try {
      // SMB fetch runs SerpAPI + full AI pipeline per review.
      // With 2 reviews × 4 LLM calls × ~4s each this can take 30–60s,
      // so we use a dedicated longer timeout for this call only.
      const response = await this.client.post('/demo/fetch-and-process', {
        business_name: request.business_name,
        location: request.location,
        limit: request.limit ?? 5,
        brand_voice: request.brand_voice ?? 'friendly, professional, empathetic',
        process_reviews: request.process_reviews ?? true,
      }, { timeout: 120000 })  // 2 minutes
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async postReply(reviewId: string, replyText: string, placeId: string, googleCredentials?: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.client.post('/demo/post-reply', {
        review_id: reviewId,
        reply_text: replyText,
        place_id: placeId,
        google_credentials: googleCredentials || undefined,
      })
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async sendSmbEmail(params: {
    review_text: string
    reviewer: string
    rating: number
    ai_response: string
    to_email: string
  }): Promise<{ token: string; email_sent: boolean }> {
    try {
      const response = await this.client.post('/reviews/smb/send-email', params)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async getGoogleOAuthUrl(): Promise<{ url: string; state: string }> {
    try {
      const response = await this.client.get('/register/google/oauth-url')
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  private handleError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        return new Error('Invalid API key')
      }
      if (error.response?.status === 404) {
        return new Error('Endpoint not found')
      }
      if (error.response?.data?.detail) {
        return new Error(error.response.data.detail)
      }
      return new Error(error.message || 'API request failed')
    }
    return new Error('Unknown error occurred')
  }
}

export const apiClient = new APIClient()
